import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { MONTHLY_STATS, MONTHLY_PEOPLE } from '@/data/monthlyReport';
import type { MonthlyClientGroup, MonthlyPersonRow, MonthlyProjectRow } from '@/data/monthlyReport';

interface MonthlyStatItem {
  label: string;
  value: string;
}

interface UseMonthlyReportResult {
  stats: readonly MonthlyStatItem[];
  people: readonly MonthlyPersonRow[];
  isLoading: boolean;
}

interface ApiMonthlyReportProject {
  projectId: string;
  projectName: string;
  billable: boolean;
  hours: number;
  eventCount: number;
}

interface ApiMonthlyReportClient {
  clientId: string;
  clientName: string;
  totalHours: number;
  projects: ApiMonthlyReportProject[];
}

interface ApiMonthlyReport {
  month: string;
  summary: {
    totalHours: number;
    billableHours: number;
    generalHours: number;
    employeeCount: number;
  };
  employees: Array<{
    id: string;
    name: string;
    totalHours: number;
    billableHours: number;
    generalHours: number;
    effectiveDays: number;
    clients?: ApiMonthlyReportClient[];
  }>;
}

function classifyProject(p: ApiMonthlyReportProject): MonthlyProjectRow {
  return {
    id: p.projectId,
    name: p.projectName,
    hours: `${p.hours.toFixed(1)}h`,
    billable: p.billable ? `${p.hours.toFixed(1)}h` : null,
    general: p.billable ? null : `${p.hours.toFixed(1)}h`,
    events: String(p.eventCount),
  };
}

/**
 * Re-shape the API's per-employee `clients` array into the page's
 * `MonthlyClientGroup` form. The page distinguishes "billable client"
 * from "general bucket" via `name === null`; the server doesn't model an
 * explicit "general" bucket, so we split each client's projects into
 * billable vs general here. Task 156 D3 — IDs flow through so the page
 * can wire client/project rows as drill-down links.
 */
function reshapeClients(clients: readonly ApiMonthlyReportClient[]): MonthlyClientGroup[] {
  const out: MonthlyClientGroup[] = [];
  for (const c of clients) {
    const billable = c.projects.filter((p) => p.billable).map(classifyProject);
    const general = c.projects.filter((p) => !p.billable).map(classifyProject);
    if (billable.length > 0) {
      out.push({ id: c.clientId, name: c.clientName, projects: billable });
    }
    if (general.length > 0) {
      // No client-level link target for the general bucket — the row is
      // plain text by leaving `id` undefined.
      out.push({ name: null, projects: general });
    }
  }
  return out;
}

/**
 * Task 156 D1 — the caller (ReportsMonthlyPage) builds the `period=…`
 * query string from the URL, defaulting to the current month when no
 * query parameters are present. Pass `null` to keep the hook disabled
 * (e.g. while the URL is still resolving or in mockup mode).
 */
export function useMonthlyReport(queryString: string | null = null): UseMonthlyReportResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['monthlyReport', orgId, queryString],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<ApiMonthlyReport>(
        `/api/v1/orgs/${orgId}/reports/monthly?${queryString}`,
        token,
      );
      const totalHours = res.summary.totalHours || 1;
      const stats: MonthlyStatItem[] = [
        { label: 'Total Hours', value: `${res.summary.totalHours.toFixed(1)}h` },
        { label: 'Billable Hours', value: `${res.summary.billableHours.toFixed(1)}h` },
        { label: 'General Hours', value: `${res.summary.generalHours.toFixed(1)}h` },
        { label: 'Team Members', value: String(res.summary.employeeCount) },
      ];
      const people: MonthlyPersonRow[] = res.employees.map((e) => ({
        id: e.id,
        name: e.name,
        hours: `${e.totalHours.toFixed(1)}h`,
        billable: `${e.billableHours.toFixed(1)}h`,
        general: `${e.generalHours.toFixed(1)}h`,
        events: String(e.effectiveDays),
        // Task 113 D4 — round to whole percent for the per-person
        // "% of Total" cell on /reports/monthly.
        pct: Math.round((e.totalHours / totalHours) * 100),
        clients: e.clients ? reshapeClients(e.clients) : undefined,
      }));
      return { stats, people };
    },
    enabled: !mockupMode && !!orgId && !!queryString,
  });

  if (mockupMode) return { stats: MONTHLY_STATS, people: MONTHLY_PEOPLE, isLoading: false };
  return {
    stats: query.data?.stats ?? [],
    people: query.data?.people ?? [],
    isLoading: query.isLoading,
  };
}
